export const DISCOUNT_TYPES = {
  RATE: 'rate',
  AMOUNT: 'amount'
}

export const DISCOUNT_SUBMIT_TYPES = {
  [DISCOUNT_TYPES.RATE]: 1,
  [DISCOUNT_TYPES.AMOUNT]: 2
}

export const DISCOUNT_CALC_TYPES = {
  1: DISCOUNT_TYPES.RATE,
  2: DISCOUNT_TYPES.AMOUNT
}

export function toThemeOrderDiscountCalcType(discountType) {
  return DISCOUNT_CALC_TYPES[discountType] || discountType
}

export function toThemeOrderDiscountSubmitType(discountType) {
  return DISCOUNT_SUBMIT_TYPES[discountType] || Number(discountType) || null
}

function toAmount(value, fieldName) {
  const amount = Number(value)
  if (!Number.isFinite(amount)) {
    throw new Error(`${fieldName}必须是有效数字`)
  }
  return amount
}

function toFixedAmount(value) {
  return Number(value).toFixed(2)
}

export function calculateThemeOrderDiscount({ receivableAmount, discountType, discountValue }) {
  const receivable = toAmount(receivableAmount, '当前拼场应收金额')
  const value = toAmount(discountValue, '折扣值')

  if (receivable <= 0) {
    throw new Error('当前拼场应收金额必须大于 0')
  }

  const calcType = toThemeOrderDiscountCalcType(discountType)
  let finalAmount

  if (calcType === DISCOUNT_TYPES.RATE) {
    if (value <= 0 || value > 99) {
      throw new Error('折扣率必须大于 0 且小于等于 99')
    }
    finalAmount = receivable * (value / 100)
  } else if (calcType === DISCOUNT_TYPES.AMOUNT) {
    if (value < 0) {
      throw new Error('优惠金额不能小于 0')
    }
    if (value > receivable) {
      throw new Error('优惠金额不能大于当前拼场应收金额')
    }
    finalAmount = receivable - value
  } else {
    throw new Error('请选择折扣方式')
  }

  if (finalAmount < 0) {
    throw new Error('折扣后金额不能小于 0')
  }

  return {
    finalAmount: toFixedAmount(finalAmount),
    discountAmount: toFixedAmount(receivable - finalAmount)
  }
}

export function hasThemeOrderMemberCard(rows = []) {
  return rows.some(item => Number(item && item.payMode) === 17 || (item && item.cardWallet && item.cardWallet.cardId))
}

export function hasThemeOrderCompletedPayment(rows = []) {
  return rows.some(item => {
    if (!item || item.payMode === '' || item.payMode === null || typeof item.payMode === 'undefined') {
      return false
    }
    const payAmount = item.payAmount
    return payAmount !== '' && payAmount !== null && typeof payAmount !== 'undefined'
  })
}

export function canApplyThemeOrderDiscount({ row, rows = [], mainRow } = {}) {
  if (!row || !row.rowId) {
    return { valid: false, message: '请选择需要设置优惠的拼场' }
  }
  if (!mainRow || !mainRow.playerNum) {
    return { valid: false, message: '请先填写当前拼场玩家人数' }
  }
  const receivableAmount = Number(mainRow.shouldPayAmount || 0)
  if (!Number.isFinite(receivableAmount) || receivableAmount <= 0) {
    return { valid: false, message: '当前拼场应收金额必须大于 0' }
  }
  if (hasThemeOrderMemberCard(rows)) {
    return { valid: false, message: '当前拼场已使用会员卡，不能使用优惠计算' }
  }
  if (hasThemeOrderCompletedPayment(rows)) {
    return { valid: false, message: '当前拼场已填写收款方式和收款金额，不能设置优惠' }
  }
  return { valid: true, message: '' }
}

export function isThemeOrderMemberCardBlockedByDiscount({ payMode, rowId, rows = [] } = {}) {
  if (Number(payMode) !== 17) {
    return false
  }
  return rows.some(item => item && item.rowId === rowId && item.amountSource === 'discount')
}

export function getThemeOrderDiscountText(row = {}) {
  if (row.amountSource !== 'discount' || !row.discountFinalAmount) {
    return '无'
  }
  return `折后 ${toFixedAmount(row.discountFinalAmount)} 元`
}

export function getThemeOrderDiscountTableText(row = {}) {
  if (row.amountSource !== 'discount' || !row.discountFinalAmount) {
    return '无'
  }

  return `${toFixedAmount(row.discountAmount || 0)}元`
}

export function getThemeOrderDiscountDetailText(row = {}) {
  if (row.amountSource !== 'discount' || !row.discountFinalAmount) {
    return '无'
  }

  const calcType = toThemeOrderDiscountCalcType(row.discountType)
  const value = row.discountValue
  if (calcType === DISCOUNT_TYPES.RATE) {
    return `拼场整体优惠率 ${value}%，优惠后 ${toFixedAmount(row.discountFinalAmount)} 元`
  }
  if (calcType === DISCOUNT_TYPES.AMOUNT) {
    return `拼场整体优惠 ${toFixedAmount(value)} 元，优惠后 ${toFixedAmount(row.discountFinalAmount)} 元`
  }
  return `优惠后 ${toFixedAmount(row.discountFinalAmount)} 元`
}

export function getThemeOrderPayTargetAmount(row = {}) {
  if (row.amountSource === 'discount' && row.discountFinalAmount !== '' && row.discountFinalAmount !== null && typeof row.discountFinalAmount !== 'undefined') {
    return toFixedAmount(row.discountFinalAmount)
  }
  return row.shouldPayAmount
}

export function isThemeOrderDiscountClearValue(value) {
  if (value === '' || value === null || typeof value === 'undefined') {
    return false
  }
  const amount = Number(value)
  return Number.isFinite(amount) && amount === 0
}

export function shouldLockThemeOrderPlayerNumByDiscount({ rowId, rows = [] } = {}) {
  const groupRows = rows.filter(item => item && item.rowId === rowId)
  const hasDiscount = groupRows.some(item => item.amountSource === 'discount')
  if (!hasDiscount) {
    return false
  }
  return hasThemeOrderCompletedPayment(groupRows)
}

export function syncThemeOrderDiscountByReceivable(row = {}) {
  if (row.amountSource !== 'discount' || !row.discountType || row.discountValue === '' || row.discountValue === null || typeof row.discountValue === 'undefined') {
    return row
  }
  if (isThemeOrderDiscountClearValue(row.discountValue)) {
    return row
  }
  const result = calculateThemeOrderDiscount({
    receivableAmount: row.shouldPayAmount,
    discountType: toThemeOrderDiscountCalcType(row.discountType),
    discountValue: row.discountValue
  })
  row.discountAmount = result.discountAmount
  row.discountFinalAmount = result.finalAmount
  Object.assign(row, buildThemeOrderDiscountSubmitFields(row))
  return row
}

export function buildThemeOrderDiscountSubmitFields(row = {}) {
  if (row.amountSource !== 'discount' || !row.discountType || !row.discountFinalAmount) {
    return {
      discountType: null,
      discountJsonStr: '',
      discountPrice: null
    }
  }

  const submitDiscountType = toThemeOrderDiscountSubmitType(row.discountType)
  const discountInfo = {
    discountType: submitDiscountType,
    discountValue: row.discountValue,
    receivableAmount: row.shouldPayAmount,
    discountAmount: row.discountAmount,
    finalAmount: row.discountFinalAmount
  }

  return {
    discountType: submitDiscountType,
    discountJsonStr: JSON.stringify(discountInfo),
    discountPrice: row.discountAmount || '0.00'
  }
}

export function hydrateThemeOrderDiscountFromServer(row = {}, serverRow = {}) {
  if (!serverRow || !serverRow.discountType) {
    return row
  }

  let discountInfo = {}
  if (serverRow.discountJsonStr) {
    try {
      discountInfo = JSON.parse(serverRow.discountJsonStr) || {}
    } catch (err) {
      discountInfo = {}
    }
  }

  const discountValue = discountInfo.discountValue
  if (discountValue === '' || discountValue === null || typeof discountValue === 'undefined') {
    return row
  }

  row.amountSource = 'discount'
  row.discountType = serverRow.discountType
  row.discountValue = discountValue
  row.discountAmount = discountInfo.discountAmount || (serverRow.discountPrice !== null && typeof serverRow.discountPrice !== 'undefined' ? toFixedAmount(serverRow.discountPrice) : '')
  row.discountFinalAmount = discountInfo.finalAmount !== null && typeof discountInfo.finalAmount !== 'undefined'
    ? toFixedAmount(discountInfo.finalAmount)
    : ''
  row.discountJsonStr = serverRow.discountJsonStr || ''
  row.discountPrice = serverRow.discountPrice !== null && typeof serverRow.discountPrice !== 'undefined'
    ? serverRow.discountPrice
    : row.discountAmount

  return row
}
